import { ProfileType } from '../../../common/enums/ProfileType';
import { InvalidUrlError } from '../../../common/errors/errors';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { FuzzerProfileMapper } from '../mappers/FuzzerProfileMapper';
import { OrganizationProfileMapper } from '../mappers/OrganizationProfileMapper';
import { TracerProfileMapper } from '../mappers/TracerProfileMapper';
import { FuzzerProfile } from '../models/FuzzerProfile';
import { OrganizationProfile } from '../models/OrganizationProfile';
import { TracerProfile } from '../models/TracerProfile';

export const GET_ORG_PROFILES_QUERY = `
query(
    $first: Int,
    $last: Int,
    $after: String,
    $before: String,
    $filters: ProfilesFilter,
    $sorters: [ProfilesSorter]
  ) {
    viewer {
      organization {
        profiles(
          first: $first,
          after: $after,
          last: $last,
          before: $before,
          filters: $filters,
          sorters: $sorters
        ) {
          totalCount
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              name
              type
              reportCount
              projectCount
              preset
              lastUsed
            }
          }
        }
      }
    }
  }
`;

export const GET_PROFILE_QUERY = `
query($profileId: ID!) {
  profile: node(id: $profileId) {
    ... on Profile {
      id
      name
      type
      preset
      ecProfile {
        lowMedThreshold
        offLowThreshold
        medHighThreshold
      }
      rsaProfile {
        lowMedThreshold
        offLowThreshold
        medHighThreshold
      }
      disabledRules {
        ruleNumber
      }
      staleKeyProfile {
        lowMedThreshold
        offLowThreshold
        medHighThreshold
      }
      certificateValidityProfile {
        lowMedThreshold
        offLowThreshold
        medHighThreshold
      }
      symmetricProfile {
        lowMedThreshold
        offLowThreshold
        medHighThreshold
      }
      expiringSoon
    }
  }
}`;

export const UPDATE_TRACER_PROFILE_QUERY = `
mutation($id: ID!, $name: String!) {
  updateTracerProfile(input: { profileId: $id, name: $name }) {
    __typename
  }
}
`;

export const CREATE_FUZZER_PROFILE_QUERY = `
mutation($name: String!, $disabledRules: [Int!]!) {
  createFuzzerProfile(input: {name: $name, disabledRules: $disabledRules}) {
    profile {
      id
    }
  }
}
`;

export const CREATE_TRACER_PROFILE_QUERY = `
mutation($input: CreateTracerProfileInput!) {
  createTracerProfile(input: $input) {
    profile {
      id
    }
  }
}
`;

export const UPDATE_FUZZER_PROFILE_QUERY = `
mutation($id: ID!, $name: String!) {
  updateFuzzerProfile(input: { profileId: $id, name: $name }) {
    __typename
  }
}
`;

export const DELETE_PROFILE_QUERY = `
mutation($id: ID!) {
  deleteProfile(input: { profileId: $id }) {
    __typename
  }
}
`;

export type ThresholdProfile = {
  medHighThreshold: number;
  lowMedThreshold: number;
  offLowThreshold: number;
};

export type CreateTracerProfilePayload = {
  name: string;
  disabledRules: number[];
  expiringSoon: number;
  symmetricProfile: ThresholdProfile;
  rsaProfile: ThresholdProfile;
  ecProfile: ThresholdProfile;
  certificateValidityProfile: ThresholdProfile;
  staleKeyProfile: ThresholdProfile;
};

export class OrganizationProfilesService extends BaseGQLService {
  private readonly profileMapper = new OrganizationProfileMapper();
  private readonly tracerProfileMapper = new TracerProfileMapper();
  private readonly fuzzerProfileMapper = new FuzzerProfileMapper();

  async getList(cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<OrganizationProfile>> {
    return this.query(
      GET_ORG_PROFILES_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.profiles;
        return {
          data: edges.map(({ node }: any) => this.profileMapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getProfile(profileId: number): Promise<TracerProfile | FuzzerProfile> {
    return this.query(
      GET_PROFILE_QUERY,
      {
        profileId: this.gqlIdEncoder.encode(ModelType.Profile, profileId),
      },
      (data) => {
        const profile = data.profile;
        if (!profile) {
          throw new InvalidUrlError(`Invalid request: could not find Profile ${profileId}`);
        }
        if (profile.type === ProfileType.TRACER) {
          return this.tracerProfileMapper.mapGQLModel(profile);
        }
        if (profile.type === ProfileType.FUZZER) {
          return this.fuzzerProfileMapper.mapGQLModel(profile);
        }
        throw new InvalidUrlError(`Invalid request: found profile type ${profile.type} in Profile ${profileId}`);
      }
    );
  }

  async createFuzzerProfile(name: string, disabledRules: number[]): Promise<number> {
    return this.mutate(
      CREATE_FUZZER_PROFILE_QUERY,
      {
        name,
        disabledRules,
      },
      (data) => this.gqlIdEncoder.decode(data.createFuzzerProfile.profile.id)
    );
  }

  async createTracerProfile(input: CreateTracerProfilePayload): Promise<number> {
    return this.mutate(
      CREATE_TRACER_PROFILE_QUERY,
      {
        input,
      },
      (data) => this.gqlIdEncoder.decode(data.createTracerProfile.profile.id)
    );
  }

  async updateProfile(profileId: number, type: ProfileType, name: string): Promise<void> {
    return this.mutate(type === ProfileType.FUZZER ? UPDATE_FUZZER_PROFILE_QUERY : UPDATE_TRACER_PROFILE_QUERY, {
      id: this.gqlIdEncoder.encode(ModelType.Profile, profileId),
      name,
    });
  }

  async deleteProfile(profileId: number): Promise<void> {
    return this.mutate(DELETE_PROFILE_QUERY, {
      id: this.gqlIdEncoder.encode(ModelType.Profile, profileId),
    });
  }

  /**
   * Profiles are returned by the API sorted by ascending ID (oldest-newest).
   * In the case of profiles, "oldest" profiles are the presets.
   * Unless explicit sorters are requested, sort profiles so that presets are displayed first, then
   * all other profiles from newest-oldest.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return [
      { field: 'preset', order: 'ASC' },
      { field: 'id', order: 'DESC' },
    ];
  }
}
